/**
 * Form Builder Constants
 *
 * Field type configurations, categories, default values, and other
 * constants used throughout the form builder.
 */

import type { FieldType, FieldCategory, FormFieldDefinition, SelectOption } from './types';

// ============================================================================
// Field Type Metadata
// ============================================================================

/**
 * Configuration for each field type
 */
export interface FieldTypeConfig {
  /** Field type identifier */
  type: FieldType;
  /** Display label */
  label: string;
  /** Localization key for label */
  labelKey: string;
  /** Icon name from the Icon component */
  icon: string;
  /** Brief description */
  description: string;
  /** Whether this is a layout element (not a form input) */
  isLayoutElement: boolean;
  /** Whether this field supports options (select, radio, etc.) */
  hasOptions: boolean;
  /** Whether this field supports min/max values */
  hasMinMax: boolean;
  /** Default value for this field type */
  defaultValue: unknown;
  /** Default placeholder text */
  defaultPlaceholder?: string;
}

/**
 * Complete configuration for all field types
 */
export const FIELD_TYPE_CONFIGS: Record<FieldType, FieldTypeConfig> = {
  // Basic Inputs
  text: {
    type: 'text',
    label: 'Text Input',
    labelKey: 'formBuilder.fields.text',
    icon: 'type',
    description: 'Single line text input',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: '',
    defaultPlaceholder: 'Enter text...',
  },
  email: {
    type: 'email',
    label: 'Email',
    labelKey: 'formBuilder.fields.email',
    icon: 'mail',
    description: 'Email address input with validation',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: '',
    defaultPlaceholder: 'Enter email...',
  },
  password: {
    type: 'password',
    label: 'Password',
    labelKey: 'formBuilder.fields.password',
    icon: 'lock',
    description: 'Secure password input',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: '',
    defaultPlaceholder: 'Enter password...',
  },
  number: {
    type: 'number',
    label: 'Number',
    labelKey: 'formBuilder.fields.number',
    icon: 'hash',
    description: 'Numeric input with optional min/max',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: true,
    defaultValue: 0,
    defaultPlaceholder: 'Enter number...',
  },
  textarea: {
    type: 'textarea',
    label: 'Text Area',
    labelKey: 'formBuilder.fields.textarea',
    icon: 'alignLeft',
    description: 'Multi-line text input',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: '',
    defaultPlaceholder: 'Enter text...',
  },

  // Selection Types
  select: {
    type: 'select',
    label: 'Dropdown',
    labelKey: 'formBuilder.fields.select',
    icon: 'chevronDown',
    description: 'Single selection dropdown',
    isLayoutElement: false,
    hasOptions: true,
    hasMinMax: false,
    defaultValue: '',
    defaultPlaceholder: 'Select an option...',
  },
  multiselect: {
    type: 'multiselect',
    label: 'Multi Select',
    labelKey: 'formBuilder.fields.multiselect',
    icon: 'listChecks',
    description: 'Multiple selection dropdown',
    isLayoutElement: false,
    hasOptions: true,
    hasMinMax: false,
    defaultValue: [],
    defaultPlaceholder: 'Select options...',
  },
  checkbox: {
    type: 'checkbox',
    label: 'Checkbox',
    labelKey: 'formBuilder.fields.checkbox',
    icon: 'checkSquare',
    description: 'Single checkbox toggle',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: false,
  },
  switch: {
    type: 'switch',
    label: 'Switch',
    labelKey: 'formBuilder.fields.switch',
    icon: 'toggleRight',
    description: 'Toggle switch',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: false,
  },
  radio: {
    type: 'radio',
    label: 'Radio Group',
    labelKey: 'formBuilder.fields.radio',
    icon: 'circle',
    description: 'Single selection from options',
    isLayoutElement: false,
    hasOptions: true,
    hasMinMax: false,
    defaultValue: '',
  },

  // Date/Time Types
  date: {
    type: 'date',
    label: 'Date',
    labelKey: 'formBuilder.fields.date',
    icon: 'calendar',
    description: 'Date picker',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: '',
    defaultPlaceholder: 'Select date...',
  },
  datetime: {
    type: 'datetime',
    label: 'Date & Time',
    labelKey: 'formBuilder.fields.datetime',
    icon: 'clock',
    description: 'Date and time picker',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: '',
    defaultPlaceholder: 'Select date and time...',
  },
  time: {
    type: 'time',
    label: 'Time',
    labelKey: 'formBuilder.fields.time',
    icon: 'clock',
    description: 'Time picker',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: '',
    defaultPlaceholder: 'Select time...',
  },

  // Media Types
  file: {
    type: 'file',
    label: 'File Upload',
    labelKey: 'formBuilder.fields.file',
    icon: 'file',
    description: 'File upload input',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: null,
  },
  image: {
    type: 'image',
    label: 'Image Upload',
    labelKey: 'formBuilder.fields.image',
    icon: 'image',
    description: 'Image upload with preview',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: null,
  },

  // Advanced Types
  slider: {
    type: 'slider',
    label: 'Slider',
    labelKey: 'formBuilder.fields.slider',
    icon: 'sliders',
    description: 'Range slider input',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: true,
    defaultValue: 0,
  },
  rating: {
    type: 'rating',
    label: 'Rating',
    labelKey: 'formBuilder.fields.rating',
    icon: 'star',
    description: 'Star rating input',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: true,
    defaultValue: 0,
  },
  color: {
    type: 'color',
    label: 'Color Picker',
    labelKey: 'formBuilder.fields.color',
    icon: 'palette',
    description: 'Color selection input',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: '#000000',
  },
  richtext: {
    type: 'richtext',
    label: 'Rich Text Editor',
    labelKey: 'formBuilder.fields.richtext',
    icon: 'edit3',
    description: 'HTML editor with formatting',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: '',
    defaultPlaceholder: 'Enter formatted text...',
  },

  // Layout Elements
  heading: {
    type: 'heading',
    label: 'Heading',
    labelKey: 'formBuilder.fields.heading',
    icon: 'heading',
    description: 'Section heading text',
    isLayoutElement: true,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: 'Section Heading',
  },
  paragraph: {
    type: 'paragraph',
    label: 'Paragraph',
    labelKey: 'formBuilder.fields.paragraph',
    icon: 'fileText',
    description: 'Static text block',
    isLayoutElement: true,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: 'Enter descriptive text here...',
  },
  divider: {
    type: 'divider',
    label: 'Divider',
    labelKey: 'formBuilder.fields.divider',
    icon: 'minus',
    description: 'Horizontal separator line',
    isLayoutElement: true,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: null,
  },
  spacer: {
    type: 'spacer',
    label: 'Spacer',
    labelKey: 'formBuilder.fields.spacer',
    icon: 'moveVertical',
    description: 'Empty space between fields',
    isLayoutElement: true,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: null,
  },

  // CCMS (Forms Management) field types
  phone: {
    type: 'phone',
    label: 'Phone Number',
    labelKey: 'formBuilder.fields.phone',
    icon: 'phone',
    description: 'Mobile / phone number',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: '',
    defaultPlaceholder: 'e.g. 082 123 4567',
  },
  participants: {
    type: 'participants',
    label: 'People / Family',
    labelKey: 'formBuilder.fields.participants',
    icon: 'users',
    description: 'Repeatable person cards (family, guests)',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: [],
  },
  location: {
    type: 'location',
    label: 'Campus / Location',
    labelKey: 'formBuilder.fields.location',
    icon: 'map-pin',
    description: 'Pick a church campus / location',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: null,
    defaultPlaceholder: 'Select a campus',
  },
  department: {
    type: 'department',
    label: 'Department / Ministry',
    labelKey: 'formBuilder.fields.department',
    icon: 'building',
    description: 'Pick a department or ministry',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: null,
    defaultPlaceholder: 'Select a department',
  },
  consent: {
    type: 'consent',
    label: 'Consent',
    labelKey: 'formBuilder.fields.consent',
    icon: 'shield-check',
    description: 'Consent checkbox with versioned text (POPIA)',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: false,
  },
  pagebreak: {
    type: 'pagebreak',
    label: 'Page Break',
    labelKey: 'formBuilder.fields.pagebreak',
    icon: 'layers',
    description: 'Split the form into steps',
    isLayoutElement: true,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: null,
  },
  address: {
    type: 'address',
    label: 'Address',
    labelKey: 'formBuilder.fields.address',
    icon: 'home',
    description: 'Street, suburb, city, postal code',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: false,
    defaultValue: null,
  },
  quantity: {
    type: 'quantity',
    label: 'Quantity',
    labelKey: 'formBuilder.fields.quantity',
    icon: 'hash',
    description: 'Number of items / tickets (optionally priced)',
    isLayoutElement: false,
    hasOptions: false,
    hasMinMax: true,
    defaultValue: 1,
  },
};

// ============================================================================
// Field Categories
// ============================================================================

/**
 * Field categories for the sidebar palette
 */
export const FIELD_CATEGORIES: FieldCategory[] = [
  {
    id: 'basic',
    label: 'Basic Inputs',
    labelKey: 'formBuilder.categories.basic',
    fields: ['text', 'email', 'password', 'number', 'textarea'],
  },
  {
    id: 'selection',
    label: 'Selection',
    labelKey: 'formBuilder.categories.selection',
    fields: ['select', 'multiselect', 'checkbox', 'switch', 'radio'],
  },
  {
    id: 'datetime',
    label: 'Date & Time',
    labelKey: 'formBuilder.categories.datetime',
    fields: ['date', 'datetime', 'time'],
  },
  {
    id: 'media',
    label: 'Media',
    labelKey: 'formBuilder.categories.media',
    fields: ['file', 'image'],
  },
  {
    id: 'advanced',
    label: 'Advanced',
    labelKey: 'formBuilder.categories.advanced',
    fields: ['slider', 'rating', 'color', 'richtext'],
  },
  {
    id: 'ccms',
    label: 'Church & Events',
    labelKey: 'formBuilder.categories.ccms',
    fields: ['phone', 'participants', 'location', 'department', 'consent', 'address', 'quantity'],
  },
  {
    id: 'layout',
    label: 'Layout',
    labelKey: 'formBuilder.categories.layout',
    fields: ['heading', 'paragraph', 'divider', 'spacer', 'pagebreak'],
  },
];

// ============================================================================
// Default Field Values
// ============================================================================

/**
 * Create a default field definition for a given type
 */
export function createDefaultField(type: FieldType, id: string): FormFieldDefinition {
  const config = FIELD_TYPE_CONFIGS[type];
  const baseName = type.replace(/([A-Z])/g, '_$1').toLowerCase();

  const field: FormFieldDefinition = {
    id,
    type,
    name: `${baseName}_${id.slice(-6)}`,
    label: config.label,
    placeholder: config.defaultPlaceholder,
    defaultValue: config.defaultValue,
    required: false,
    colSpan: 1,
  };

  // Add type-specific defaults
  if (config.hasOptions) {
    field.options = getDefaultOptions(type);
  }

  if (config.hasMinMax) {
    switch (type) {
      case 'number':
        field.min = 0;
        field.max = 100;
        break;
      case 'slider':
        field.min = 0;
        field.max = 100;
        field.step = 1;
        break;
      case 'rating':
        field.min = 0;
        field.max = 5;
        break;
      case 'quantity':
        field.min = 1;
        field.max = 10;
        field.step = 1;
        break;
    }
  }

  // CCMS field defaults
  if (type === 'participants') {
    field.label = 'People attending';
    field.minItems = 1;
    field.maxItems = 6;
    field.relationshipOptions = [
      { value: 'Spouse', label: 'Spouse' },
      { value: 'Child', label: 'Child' },
      { value: 'Guest', label: 'Guest' },
    ];
    field.participantFields = [
      { id: `${id}_fn`, type: 'text', name: 'firstName', label: 'First name', required: true, memberField: 'Member.FirstName' },
      { id: `${id}_sn`, type: 'text', name: 'surname', label: 'Surname', required: true, memberField: 'Member.LastName' },
    ];
  } else if (type === 'consent') {
    field.label = 'I agree';
    field.required = true;
    field.consentText = 'I consent to my personal information being processed for this purpose.';
    field.consentVersion = '1';
  } else if (type === 'pagebreak') {
    field.label = 'Next page';
  } else if (type === 'phone') {
    field.memberField = 'Member.CellNumber';
  }

  // Layout element specifics
  if (type === 'heading') {
    field.headingLevel = 2;
    field.label = 'Section Heading';
  } else if (type === 'paragraph') {
    field.content = 'Enter descriptive text here...';
  } else if (type === 'spacer') {
    field.spacerHeight = 24;
  } else if (type === 'textarea') {
    field.rows = 4;
  }

  return field;
}

/**
 * Get default options for selection fields
 */
function getDefaultOptions(type: FieldType): SelectOption[] {
  return [
    { value: 'option1', label: 'Option 1' },
    { value: 'option2', label: 'Option 2' },
    { value: 'option3', label: 'Option 3' },
  ];
}

// ============================================================================
// Validation Presets
// ============================================================================

/**
 * Common validation rule presets
 */
export const VALIDATION_PRESETS = {
  email: {
    type: 'email' as const,
    message: 'Please enter a valid email address',
    messageKey: 'formBuilder.validation.email',
  },
  url: {
    type: 'url' as const,
    message: 'Please enter a valid URL',
    messageKey: 'formBuilder.validation.url',
  },
  required: {
    type: 'required' as const,
    message: 'This field is required',
    messageKey: 'formBuilder.validation.required',
  },
  minLength: (value: number) => ({
    type: 'minLength' as const,
    value,
    message: `Minimum ${value} characters required`,
    messageKey: 'formBuilder.validation.minLength',
  }),
  maxLength: (value: number) => ({
    type: 'maxLength' as const,
    value,
    message: `Maximum ${value} characters allowed`,
    messageKey: 'formBuilder.validation.maxLength',
  }),
  min: (value: number) => ({
    type: 'min' as const,
    value,
    message: `Minimum value is ${value}`,
    messageKey: 'formBuilder.validation.min',
  }),
  max: (value: number) => ({
    type: 'max' as const,
    value,
    message: `Maximum value is ${value}`,
    messageKey: 'formBuilder.validation.max',
  }),
};

// ============================================================================
// Keyboard Shortcuts
// ============================================================================

/**
 * Keyboard shortcuts for the form builder
 */
export const KEYBOARD_SHORTCUTS = {
  undo: { key: 'z', modifiers: ['ctrl'] },
  redo: { key: 'y', modifiers: ['ctrl'] },
  redoAlt: { key: 'z', modifiers: ['ctrl', 'shift'] },
  delete: { key: 'Delete', modifiers: [] },
  deleteAlt: { key: 'Backspace', modifiers: [] },
  copy: { key: 'c', modifiers: ['ctrl'] },
  paste: { key: 'v', modifiers: ['ctrl'] },
  duplicate: { key: 'd', modifiers: ['ctrl'] },
  selectAll: { key: 'a', modifiers: ['ctrl'] },
  escape: { key: 'Escape', modifiers: [] },
  save: { key: 's', modifiers: ['ctrl'] },
  preview: { key: 'p', modifiers: ['ctrl', 'shift'] },
};

// ============================================================================
// Layout Constants
// ============================================================================

/**
 * Column span options
 */
export const COLUMN_SPAN_OPTIONS = [
  { value: '1', label: '1 Column' },
  { value: '2', label: '2 Columns' },
  { value: '3', label: '3 Columns' },
  { value: '4', label: 'Full Width' },
];

/**
 * Form column options
 */
export const FORM_COLUMN_OPTIONS = [
  { value: '1', label: '1 Column' },
  { value: '2', label: '2 Columns' },
  { value: '3', label: '3 Columns' },
  { value: '4', label: '4 Columns' },
];

/**
 * Gap size options
 */
export const GAP_OPTIONS = [
  { value: 'xs', label: 'Extra Small' },
  { value: 'sm', label: 'Small' },
  { value: 'md', label: 'Medium' },
  { value: 'lg', label: 'Large' },
  { value: 'xl', label: 'Extra Large' },
];

/**
 * Submit button position options
 */
export const BUTTON_POSITION_OPTIONS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
  { value: 'stretch', label: 'Full Width' },
];

/**
 * Heading level options
 */
export const HEADING_LEVEL_OPTIONS = [
  { value: '1', label: 'H1 - Largest' },
  { value: '2', label: 'H2 - Large' },
  { value: '3', label: 'H3 - Medium' },
  { value: '4', label: 'H4 - Small' },
  { value: '5', label: 'H5 - Smaller' },
  { value: '6', label: 'H6 - Smallest' },
];

// ============================================================================
// Drag & Drop Constants
// ============================================================================

/**
 * Data transfer type for drag operations
 */
export const DND_ITEM_TYPE = 'application/x-form-builder-field';

/**
 * Drag source types
 */
export const DRAG_SOURCE = {
  PALETTE: 'palette',
  CANVAS: 'canvas',
} as const;

// ============================================================================
// Storage Keys
// ============================================================================

/**
 * Local storage keys
 */
export const STORAGE_KEYS = {
  FORM_BUILDER_STATE: 'ccms_form_builder_state',
  FORM_CLIPBOARD: 'ccms_form_clipboard',
  RECENT_FORMS: 'ccms_recent_forms',
} as const;

// ============================================================================
// History Constants
// ============================================================================

/**
 * Maximum number of history entries to keep
 */
export const MAX_HISTORY_SIZE = 50;
